// Delivery type vocabulary shared by the delivery-picking surfaces
// (`DeliverySection`, `DeliveryModePills`): the mode literal, labels and
// canonical ordering, and the selection the section reports.

import type { components } from "./api-schema.generated";

type Schemas = components["schemas"];

export type DeliveryMode =
  "tbc" | "ship" | "collect" | "multi" | "hold" | "mailing";

export const DELIVERY_MODE_LABELS: Record<DeliveryMode, string> = {
  tbc: "Quote only",
  ship: "Ship",
  collect: "Collect",
  multi: "Multi-destination",
  hold: "Hold",
  mailing: "Mailing",
};

/** Canonical mode pill order — used by `DeliveryModePills` regardless
 *  of which subset a consumer chooses to render. */
export const DELIVERY_MODE_ORDER: readonly DeliveryMode[] = [
  "ship",
  "multi",
  "collect",
  "mailing",
  "hold",
  "tbc",
];

/** A delivery's service level, by its code ("next_day"); its words are the
 *  server's (`service_level_label`), never rebuilt here. */
export type DeliveryServiceLevel = Schemas["DeliveryServiceLevel"];

// ── DeliverySelection ─────────────────────────────────────────────────
//
// The flat delivery choice `DeliverySection` reports (order to invoice doc
// 104 §9.1). On the catalogue item page it is the line's delivery choice,
// sent to the price preview and the basket as `DeliverySelectionSchema`
// (`deliverySelectionBody`); the merchandise request flow sends only its
// mode.

export interface DeliverySelection {
  /** Which mode the operator picked: drives which fields below mean anything. */
  mode: DeliveryMode;
  /** The CIS address (customer spine) the goods go to. Set only for Ship and
   *  Multi (its first row) on the spine's address book; null otherwise. */
  deliveryAddressId: string | null;
  /** The courier option picked from the priced list: its level and courier.
   *  Null for the default (the cheapest non-timed option, the server's). */
  serviceLevel: DeliveryServiceLevel | null;
  courierSupplierId: string | null;
  /** "Agreed price" is chosen: the price and reason below are what has been
   *  entered so far, either of which may still be missing. */
  isAgreedPrice: boolean;
  agreedDeliveryPricePence: number | null;
  agreedDeliveryReason: string | null;
  /** Staff-entered packages for a delivery not yet priced because an item
   *  has no weight or packing; sent only together. */
  weightKg: number | null;
  parcels: number | null;
  /** Collect: the collection point (a CIS address of ours) and who collects. */
  collectionPointAddressId: string | null;
  recipientName: string | null;
  recipientPhone: string | null;
  /** Multi-destination: how many go to each address, in the rows' order
   *  (order to invoice doc 106 §3). Each address's service is chosen at
   *  checkout; absent or empty for every other mode. */
  deliveryLines?: readonly DeliveryLineSelection[];
}

/** One row of a line split across addresses: a CIS address (null until one
 *  is picked) and how many go there. */
export interface DeliveryLineSelection {
  deliveryAddressId: string | null;
  quantity: number;
}
